/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useContext, useEffect, type ReactNode } from 'react';
import { getAuthData, clearAuthData } from '@/utils/auth';
import { type AuthData, type User } from '@dc-trans/data-layer';
import queryClient from '@/libs/queryClient';
import { useSelectOptionsStore } from '@/stores/selectOptionsStore';
import { useUIStore } from '@/stores/uiStore';

interface AuthContextType {
  user?: User;
  isLogoutManually?: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (_userData?: AuthData) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: undefined,
  isLogoutManually: false,
  isAuthenticated: false,
  isLoading: true,
  login: (_userData?: AuthData) => {},
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLogoutManually, setIsLogoutManually] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth data on mount and refresh token if available
  useEffect(() => {
    const initializeAuth = () => {
      const authData = getAuthData();

      if (authData?.access_token) {
        setIsAuthenticated(true);
        if (authData.user) {
          setUser(authData.user);
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (authData?: AuthData) => {
    if (authData) {
      setUser(authData.user);
    }
    setIsAuthenticated(true);
  };

  const logout = () => {
    setIsLogoutManually(true);
    clearAuthData();
    queryClient.clear();
    useSelectOptionsStore.getState().reset();
    useUIStore.getState().reset();
    setUser(undefined);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, isLogoutManually }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
